import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { GoodieFile, useUploadFile } from '@/features/files'
import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  name: string
  cover: GoodieFile
  sortBy: GraphQL.CategorySortTypes
  translations: FormTranslations
}

export const TRANSLATABLE_FIELDS: (keyof FormData)[] = ['name']

interface Options {
  layoutId?: number
  id?: number
  onDone?: () => void
}

export default function useCategoryForm(options: Options) {
  const { layoutId, id, onDone } = options

  const { t } = useLingui()
  const [uploadFile] = useUploadFile()

  const [createCategory] = useMutation(AdminAPI.category.CREATE, {
    refetchQueries: [AdminAPI.category.GET_BY_LAYOUT]
  })

  const [updateCategory] = useMutation(AdminAPI.category.UPDATE, {
    refetchQueries: [AdminAPI.category.GET_BY_LAYOUT]
  })

  const { parseApolloErrors, errors } = Form.useErrorHelpers({
    mapper(field, message) {
      if (field === 'slug' && message === 'has already been taken') {
        return [
          'name',
          {
            type: message,
            message: t`The category name exists already.`
          }
        ]
      }
    }
  })

  const create = useCallback(
    async ({ cover, ...rest }: FormData) => {
      if (!layoutId) return

      try {
        const { id: coverId } = await uploadFile(cover)

        const response = await createCategory({
          variables: {
            layoutId,
            input: {
              ...rest,
              coverId,
              translations: formToGraphql(rest.translations)
            }
          }
        })

        if (response.errors) {
          parseApolloErrors(response.errors)

          return
        }

        onDone?.()
      } catch (error) {
        if (error instanceof ApolloError) {
          parseApolloErrors(error.graphQLErrors)

          return
        }

        throw error
      }
    },
    [layoutId, createCategory, uploadFile, onDone, parseApolloErrors]
  )

  const update = useCallback(
    async ({ cover, ...rest }: FormData) => {
      if (!id) return

      try {
        const { id: coverId } = await uploadFile(cover)

        const response = await updateCategory({
          variables: {
            input: {
              ...rest,
              id,
              coverId,
              translations: formToGraphql(rest.translations)
            }
          }
        })

        if (response.errors) {
          parseApolloErrors(response.errors)

          return
        }

        onDone?.()
      } catch (error) {
        if (error instanceof ApolloError) {
          parseApolloErrors(error.graphQLErrors)

          return
        }

        throw error
      }
    },
    [id, updateCategory, uploadFile, onDone, parseApolloErrors]
  )

  return {
    create,
    update,
    errors
  }
}
